import React, { useState, useEffect, useRef } from 'react';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatDesignStyles } from '../../components/DesignListingCard/DesignListingCard';
import { recordLaunchEvent } from '../../util/api';
import {
  LAUNCH_EVENT_VIEW,
  LAUNCH_LISTING_TYPE,
  isMatteraxAdminId,
} from '../../config/configWhatsNew';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  manageDisableScrolling,
  isScrollingDisabled,
  showVerifyEmailGate,
  isEmailUnverified,
} from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { selectBoards, boardContainsVariant } from '../../ducks/boards.duck';
import { variantDisplayTitle } from '../../util/variantHelpers';

// Shared components
import {
  H2,
  H4,
  H3,
  Modal,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  AvatarLarge,
  ResponsiveImage,
  FoundingBadge,
} from '../../components';
import { pathByRouteName } from '../../util/routes';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';

import SaveButton from '../../components/SaveButton/SaveButton';
import AddToBoardModal from '../../components/AddToBoardModal/AddToBoardModal';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionFeaturedProjectsMaybe from './SectionFeaturedProjectsMaybe';
import SectionSimilarListingsMaybe from './SectionSimilarListingsMaybe';
// SectionMapMaybe import removed — see comment near render call.
import SectionGallery from './SectionGallery';
import SectionLaunchLayout from './SectionLaunchLayout';
import SectionVariantPicker from './SectionVariantPicker';
import CustomListingFields from './CustomListingFields';
import SectionFixedSpecs from './SectionFixedSpecs';
import {
  SpecsBody,
  CertificationsBody,
  FeaturesBody,
  FilesBody,
  IncludedWarrantyBody,
  hasIncludedWarranty,
} from './SectionStoneSpecs';
import CollapsibleSection from './CollapsibleSection';
import Breadcrumbs from './Breadcrumbs';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);
  const [boardModalOpen, setBoardModalOpen] = useState(false);
  // Design showcase gallery: active hero image index (thumbnail strip below)
  const [designImageIdx, setDesignImageIdx] = useState(0);
  // Cross-highlight between photo pins and the materials rail
  const [activeProductId, setActiveProductId] = useState(null);
  // hover-intent timer for the materials rail (prevents hero strobing while
  // the cursor travels across cards)
  const railHoverTimer = useRef(null);
  // pin-hover: rail scrollport ref + intent timer, so hovering a pin nudges
  // ONLY the rail's own scroll (never the page) and only after the pointer
  // rests on the pin
  const railListRef = useRef(null);
  const pinHoverTimer = useRef(null);
  // material quick-view modal (opened by clicking a rail card or a photo pin)
  const [quickViewProductId, setQuickViewProductId] = useState(null);
  const [quickViewImageIdx, setQuickViewImageIdx] = useState(0);
  // viewer control: hide the in-photo product pins for a clean look at the photo
  const [pinsHidden, setPinsHidden] = useState(false);
  // Design hero swipe carousel: native scroll-snap track; swiping keeps the
  // photo counter + thumb highlight in sync. Native listener (not React
  // onScroll) because scroll events don't bubble, so the delegated synthetic
  // handler misses real touch swipes. No deps: re-attach each render so the
  // listener follows the track as it mounts/unmounts (cheap — one listener).
  const designHeroTrackRef = useRef(null);
  useEffect(() => {
    const el = designHeroTrackRef.current;
    if (!el) return;
    const onScroll = () => {
      const count = el.children.length;
      const idx = Math.min(count - 1, Math.max(0, Math.round(el.scrollLeft / el.clientWidth)));
      setDesignImageIdx(prev => (idx !== prev ? idx : prev));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  });
  // The track's height follows the ACTIVE photo. A flex track is otherwise as
  // tall as its tallest rendered slide, so a project mixing landscape and
  // portrait photos left a big gap under the short ones (and the counter,
  // anchored to the track bottom, floated down in it). ResizeObserver keeps
  // the height honest while the photo is still loading in. No deps — like the
  // scroll listener above, the track mounts only after the listing loads, so
  // a deps-gated effect would run once against nothing and never re-arm.
  useEffect(() => {
    const el = designHeroTrackRef.current;
    if (!el) return;
    const slide = el.children[designImageIdx];
    const anchor = slide && slide.firstElementChild; // the pin anchor wraps the img
    const update = () => {
      const h = anchor ? anchor.offsetHeight : 0;
      if (h > 0) el.style.height = `${h}px`;
    };
    update();
    if (!anchor || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(anchor);
    return () => ro.disconnect();
  });
  // Material quick-view: same swipe-carousel treatment for the product photos
  const quickViewTrackRef = useRef(null);
  useEffect(() => {
    const el = quickViewTrackRef.current;
    if (!el) return;
    const onScroll = () => {
      const count = el.children.length;
      const idx = Math.min(count - 1, Math.max(0, Math.round(el.scrollLeft / el.clientWidth)));
      setQuickViewImageIdx(prev => (idx !== prev ? idx : prev));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  });
  // opening a different material snaps its carousel back to the first photo
  useEffect(() => {
    const el = quickViewTrackRef.current;
    if (el) el.scrollTo({ left: 0 });
  }, [quickViewProductId]);
  // mobile quick-view sheet: swipe-down to dismiss (replaces the ✕ there)
  const sheetDrag = useRef({ startY: 0, el: null, dragging: false });
  // Variant picker state — fed by SectionVariantPicker.onVariantChange.
  // Stays null on parallel-SKU listings (picker renders nothing), so every
  // variant-aware branch below is an automatic no-op for the flat catalogue.
  const [activeVariant, setActiveVariant] = useState(null);
  // Hero-swipe → picker sync: swiping the gallery to another colourway's
  // photo reports its SKU here, and SectionVariantPicker follows (syncSku).
  const [gallerySwipeSku, setGallerySwipeSku] = useState(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const dispatch = useDispatch();

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    boards,
    designProductListings,
    relatedLaunchListings,
    similarListings,
    similarListingsInProgress,
    similarListingsSource,
    ...restOfProps
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  // Variant deep-link: a ?sku= QUERY param opens the listing with that
  // variant preselected. `variant` is a RESERVED PATH param (the draft /
  // pending-approval route — see isVariant above), so the deep-link MUST be
  // a query param, never a path segment.
  const initialVariantSku = new URLSearchParams(location?.search || '').get('sku') || undefined;

  // Keep ?sku= in sync with the active variant so a selected colour/finish is
  // shareable. Guarded by sku → only replaces history when the selection
  // actually changes; entirely inert for parallel-SKU listings (activeVariant
  // stays null). history.replace (not push) so the back button isn't polluted.
  const activeVariantSku = activeVariant?.sku;

  // "Added to board" reflects the ACTIVE variant, not the whole product —
  // boarding Beige must not light up the button while viewing Navy. Computed in
  // the component (not mapStateToProps) because it depends on activeVariant
  // state. Flat listings (activeVariantSku undefined) fall back to whole-listing
  // membership.
  // currentListing is ensureListing(getListing(id)), whose shell has id: null
  // whenever the listing is not (yet) in state.marketplaceData.entities. That is
  // the normal state on the FIRST render of this route — loadData only runs in
  // componentDidMount — and it is guaranteed when arriving from the visual
  // search modal, whose results come from /api/image-search and are therefore
  // never merged into the entity store. Reading currentListing.id.uuid here
  // threw before the "still loading" guard 48 lines below could catch it.
  const isAddedToBoard =
    currentListing.id && Array.isArray(boards)
      ? boards.some(b => boardContainsVariant(b, currentListing.id.uuid, activeVariantSku))
      : false;
  useEffect(() => {
    if (!activeVariantSku) return;
    const sp = new URLSearchParams(location?.search || '');
    if (sp.get('sku') === activeVariantSku) return;
    sp.set('sku', activeVariantSku);
    history.replace({ search: sp.toString() });
  }, [activeVariantSku, location, history]);

  // "What's New" launch pages report their own view to the engagement
  // warehouse. This is SEPARATE from the metadata view counter loadData
  // already fires for every listing: that one is a display number and is
  // anonymously spoofable, while these rows are session-verified and are what
  // the rails rank on. Computed here — before the early returns below, so the
  // hook order never changes — and gated on the same admin-author allowlist as
  // the editorial layout itself, so a spoofed launch listing can't write rows
  // into the curated dataset.
  const launchViewId =
    currentListing?.attributes?.publicData?.listingType === LAUNCH_LISTING_TYPE &&
    isMatteraxAdminId(currentListing?.author?.id)
      ? currentListing.id?.uuid
      : null;
  useEffect(() => {
    // Once per mount (the id is stable for the life of the page). Client-side
    // only: this fires from an effect, which never runs during SSR.
    if (!launchViewId) return;
    recordLaunchEvent({ launchId: launchViewId, event: LAUNCH_EVENT_VIEW }).catch(() => {});
  }, [launchViewId]);

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  // Keep the topbar search pre-tagged to "Designs" while viewing a design
  // project so a follow-up search stays within designs (product listings keep
  // the default catalog scope).
  const isDesignShowcaseListing =
    currentListing?.attributes?.publicData?.listingType === 'design-showcase';
  const topbar = <TopbarContainer searchScope={isDesignShowcaseListing ? 'designs' : null} />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  // Matterax design showcase: design listings get a project-page layout
  // (photo story + designer card) instead of the product layout.
  const isDesignShowcase = publicData?.listingType === 'design-showcase';

  // Matterax "What's New": launches get an editorial layout — but ONLY when
  // the author is on the admin allowlist.
  //
  // THE SPOOF GATE. Sharetribe's token model lets any user publish a listing
  // with publicData.listingType = 'launch' straight through the SDK; the
  // wizard offers the type to admins only, but the API doesn't care what the
  // wizard offers. Without the author check such a listing would render in
  // Matterax's own editorial chrome — brand voice, "why it matters",
  // launch badges — which is precisely the credibility a spoof would be
  // after. With it, a spoofed launch falls through to the STANDARD listing
  // layout: it stays visible and perfectly usable, it just looks like what it
  // is, an ordinary listing.
  const isLaunch =
    publicData?.listingType === LAUNCH_LISTING_TYPE &&
    isMatteraxAdminId(currentListing?.author?.id);
  const DESIGN_PROPERTY_LABELS = {
    condominium: 'Condominium',
    apartment: 'Apartment',
    terrace: 'Terrace house',
    'semi-detached': 'Semi-detached',
    bungalow: 'Bungalow',
    shophouse: 'Shophouse',
    office: 'Office',
    retail: 'Retail / F&B',
    other: 'Other',
  };
  const DESIGN_MONTH_LABELS = {
    '01': 'January',
    '02': 'February',
    '03': 'March',
    '04': 'April',
    '05': 'May',
    '06': 'June',
    '07': 'July',
    '08': 'August',
    '09': 'September',
    '10': 'October',
    '11': 'November',
    '12': 'December',
  };
  const DESIGN_SERVICE_LABELS = {
    design: 'Design',
    'design-build': 'Design & build',
  };
  const DESIGN_BUDGET_LABELS = {
    'under-50k': 'Under RM 50k',
    '50k-150k': 'RM 50k – 150k',
    '150k-250k': 'RM 150k – 250k',
    '250k-500k': 'RM 250k – 500k',
    'above-500k': 'RM 500k+',
  };
  const designFactRows = isDesignShowcase
    ? [
        {
          key: 'style',
          label: 'Design style',
          value: formatDesignStyles(publicData?.designStyle),
        },
        {
          key: 'service',
          label: 'Service',
          value: DESIGN_SERVICE_LABELS[(publicData?.serviceType)],
        },
        {
          key: 'property',
          label: 'Property type',
          value: DESIGN_PROPERTY_LABELS[(publicData?.propertyType)],
        },
        {
          key: 'location',
          label: 'Location',
          value:
            typeof publicData?.projectLocation === 'string' && publicData.projectLocation.trim()
              ? publicData.projectLocation.trim()
              : null,
        },
        {
          key: 'floorArea',
          label: 'Floor area',
          value: publicData?.floorAreaSqft
            ? `${Number(publicData.floorAreaSqft).toLocaleString('en-US')} sq ft`
            : null,
        },
        {
          key: 'duration',
          label: 'Duration',
          value:
            typeof publicData?.projectDurationWeeks === 'number' &&
            isFinite(publicData.projectDurationWeeks) &&
            publicData.projectDurationWeeks > 0
              ? `${publicData.projectDurationWeeks} ${
                  publicData.projectDurationWeeks === 1 ? 'week' : 'weeks'
                }`
              : null,
        },
        {
          key: 'budget',
          label: 'Renovation budget',
          // raw RM figure (rounded to 10k) preferred; legacy bucket fallback
          value:
            (typeof publicData?.renovationBudgetRm === 'number' &&
            isFinite(publicData.renovationBudgetRm) &&
            publicData.renovationBudgetRm > 0
              ? publicData.renovationBudgetRm >= 1000000
                ? `RM ${(publicData.renovationBudgetRm / 1000000).toFixed(1).replace(/\.0$/, '')}m`
                : `RM ${Math.round(publicData.renovationBudgetRm / 1000)}k`
              : null) || DESIGN_BUDGET_LABELS[(publicData?.renovationBudget)],
        },
        {
          key: 'completed',
          label: 'Completed',
          value: publicData?.completedYear
            ? [DESIGN_MONTH_LABELS[(publicData?.completedMonth)], publicData.completedYear]
                .filter(Boolean)
                .join(' ')
            : null,
        },
        {
          key: 'materials',
          label: 'Materials tagged',
          value:
            Array.isArray(publicData?.designProducts) && publicData.designProducts.length > 0
              ? `${publicData.designProducts.length}`
              : null,
        },
      ].filter(r => r.value)
    : [];

  // Dynamic title: track the selected variant's colourway. The listing title is
  // "Product — DefaultVariant"; swap that suffix for the ACTIVE variant's label
  // so the H1 follows the picker. Flat listings (activeVariant null) and titles
  // without a " — " separator fall back to the raw title unchanged.
  const activeVariantLabel = activeVariant ? activeVariant.label || activeVariant.sku : null;
  const displayTitle = variantDisplayTitle(title, activeVariantLabel);

  const richTitle = (
    <span>
      {richText(displayTitle, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
    activeVariant,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  // off-catalog swatch images must not surface as social/SEO images for designs
  const seoOffImageIds = isDesignShowcase ? publicData?.offCatalogImageIds || [] : [];
  const seoListing =
    seoOffImageIds.length > 0
      ? {
          ...currentListing,
          images: (currentListing.images || []).filter(
            img => !seoOffImageIds.includes(img.id?.uuid)
          ),
        }
      : currentListing;
  const facebookImages = listingImages(seoListing, 'facebook');
  const twitterImages = listingImages(seoListing, 'twitter');
  const schemaImages = listingImages(
    seoListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

  // ---- Launch SEO ----------------------------------------------------------
  // A launch is an ARTICLE, not a Product: it has no price, no stock and no
  // offer, and marking it up as a Product would put an offer-less Product in
  // the index and describe editorial coverage as a thing for sale. Article
  // also carries datePublished, which is the fact that actually matters about
  // a launch.
  //
  // The title and description come from the launch's own copy (subtitle, then
  // editorial summary) rather than the generic listing schemaTitle, which
  // reads "Title, price — Marketplace" and would render "undefined" pricing
  // for a type that has none.
  const launchSeoText = value => (typeof value === 'string' && value.trim() ? value.trim() : null);
  const launchDescription = isLaunch
    ? launchSeoText(publicData?.subtitle) ||
      launchSeoText(publicData?.editorialSummary) ||
      launchSeoText(description)
    : null;
  const pageTitle = isLaunch
    ? intl.formatMessage({ id: 'LaunchPage.schemaTitle' }, { title, marketplaceName })
    : schemaTitle;
  const pageDescription = isLaunch ? launchDescription || description : description;
  const launchPublishedIso = isLaunch ? launchSeoText(publicData?.publishedAtIso) : null;
  const pageSchema = isLaunch
    ? {
        '@context': 'http://schema.org',
        '@type': 'Article',
        headline: title,
        description: pageDescription,
        image: schemaImages,
        mainEntityOfPage: productURL,
        // Omitted rather than faked when the stamp is missing (a launch seeded
        // through the Integration API carries no publishedAtIso).
        ...(launchPublishedIso ? { datePublished: launchPublishedIso } : {}),
        publisher: { '@type': 'Organization', name: marketplaceName },
      }
    : {
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      };

  // Bring a material card into the rail's visible band by scrolling the rail
  // list itself. scrollIntoView would also scroll the page (it walks every
  // scrollable ancestor), which jerks the viewport on pin hover.
  const scrollRailRowIntoView = productId => {
    const list = railListRef.current;
    const row =
      typeof document !== 'undefined'
        ? document.getElementById(`design-material-${productId}`)
        : null;
    if (!list || !row) return;
    const listRect = list.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const delta =
      rowRect.top < listRect.top + 4
        ? rowRect.top - listRect.top - 8
        : rowRect.bottom > listRect.bottom - 4
        ? rowRect.bottom - listRect.bottom + 8
        : 0;
    // smooth scrolls self-interrupt: a newer hover target simply redirects
    // the glide, so rapid pin-hopping never queues a scroll backlog
    if (delta !== 0) list.scrollBy({ top: delta, behavior: 'smooth' });
  };

  const openMaterialQuickView = productId => {
    setQuickViewImageIdx(0);
    setQuickViewProductId(productId);
  };
  // Plain left-click opens the quick view; modified clicks (cmd/ctrl/shift/alt
  // or middle button) fall through to the browser and deep-link the product.
  const quickViewClickHandler = productId => e => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    openMaterialQuickView(productId);
  };
  const offCatalogProducts = isDesignShowcase ? publicData?.offCatalogProducts || [] : [];
  const offCatalogImageIds = isDesignShowcase ? publicData?.offCatalogImageIds || [] : [];
  // off-catalog swatch images live on the listing but are not project photos
  const designGalleryImages = isDesignShowcase
    ? (currentListing.images || []).filter(img => !offCatalogImageIds.includes(img.id?.uuid))
    : currentListing.images || [];
  // Slides a materials-rail hover can jump to: each material's FIRST pin photo.
  // These mount their hero image eagerly alongside the current ±1 window, so a
  // hover jump lands on an already-fetched photo instead of a just-mounted blank
  // (the windowing exists so an 18-photo project doesn't fetch everything; this
  // only widens it by the handful of pinned hero photos).
  const firstPinImageIds = new Set();
  if (isDesignShowcase) {
    const seenPinProducts = new Set();
    (publicData?.designPins || []).forEach(pin => {
      if (!seenPinProducts.has(pin.productId)) {
        seenPinProducts.add(pin.productId);
        if (pin.imageId) firstPinImageIds.add(pin.imageId);
      }
    });
  }
  const pinJumpIdxSet = new Set(
    designGalleryImages
      .map((img, i) => (firstPinImageIds.has(img.id?.uuid) ? i : -1))
      .filter(i => i >= 0)
  );
  const quickViewListing = isDesignShowcase
    ? (designProductListings || []).find(l => l.id.uuid === quickViewProductId)
    : null;
  const quickViewOffTag =
    isDesignShowcase &&
    typeof quickViewProductId === 'string' &&
    quickViewProductId.startsWith('off_')
      ? offCatalogProducts.find(t => t.id === quickViewProductId)
      : null;
  const quickViewOffImage = quickViewOffTag?.imageId
    ? (currentListing.images || []).find(img => img.id?.uuid === quickViewOffTag.imageId)
    : null;
  const quickViewTag = isDesignShowcase
    ? (publicData?.designProducts || []).find(t => t.id === quickViewProductId)
    : null;
  const quickViewBrand =
    quickViewListing?.author?.attributes?.profile?.displayName ||
    quickViewListing?.attributes?.publicData?.brand ||
    quickViewListing?.attributes?.publicData?.brand_slug ||
    null;
  const quickViewPath = quickViewListing
    ? pathByRouteName('ListingPage', routeConfiguration, {
        id: quickViewListing.id.uuid,
        slug: createSlug(quickViewListing.attributes?.title || 'listing'),
      })
    : null;

  return (
    <Page
      title={pageTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={pageDescription}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      {...(isLaunch ? { openGraphType: 'article' } : {})}
      {...(launchPublishedIso ? { published: launchPublishedIso } : {})}
      schema={pageSchema}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        {isLaunch ? (
          <SectionLaunchLayout
            listing={currentListing}
            currentUser={currentUser}
            isOwnListing={isOwnListing}
            relatedProducts={designProductListings || []}
            relatedLaunches={relatedLaunchListings || []}
            routeConfiguration={routeConfiguration}
            onManageDisableScrolling={onManageDisableScrolling}
            actionBar={
              mounted && currentListing.id ? (
                <ActionBarMaybe
                  className={css.actionBarForProductLayout}
                  isOwnListing={isOwnListing}
                  listing={currentListing}
                  currentUser={currentUser}
                  editParams={{
                    id: listingId.uuid,
                    slug: listingSlug,
                    type: listingPathParamType,
                    tab: listingTab,
                  }}
                />
              ) : null
            }
          />
        ) : isDesignShowcase ? (
          <div className={css.designPageWrapper}>
            {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                currentUser={currentUser}
              />
            ) : null}
            {mounted && currentListing.id ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                currentUser={currentUser}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
              />
            ) : null}
            <div className={css.designColumns}>
              <div className={css.designMainCol}>
                <header className={css.designHeader}>
                  <div className={css.designHeaderMain}>
                    <H2 as="h1" className={css.designTitle}>
                      {title}
                    </H2>
                    <div className={css.designByline}>
                      {ensuredAuthor?.id?.uuid ? (
                        <NamedLink
                          className={css.designBylineLink}
                          name="ProfilePage"
                          params={{ id: ensuredAuthor.id.uuid }}
                        >
                          <span className={css.designBylinePrefix}>By</span>
                          <span className={css.designBylineName}>{authorDisplayName}</span>
                          <FoundingBadge user={ensuredAuthor} />
                        </NamedLink>
                      ) : null}
                    </div>
                  </div>
                </header>
                {showListingImage ? (
                  <div className={css.designGallery}>
                    <div className={css.designMainImage}>
                      {/* Swipe carousel: native scroll-snap track, one photo per
                        gesture; the counter + thumb highlight track the swipe.
                        Each slide has its own shrink-wrapped anchor whose box IS
                        that photo's rendered box, so pin percentages stay
                        faithful for any aspect ratio and pins ride along with
                        their photo. Slides are windowed (current ±1) so an
                        18-photo project doesn't fetch everything up front. */}
                      <div className={css.designHeroTrack} ref={designHeroTrackRef}>
                        {designGalleryImages.map((img, imgIdx) => (
                          <div key={img.id?.uuid || imgIdx} className={css.designHeroSlide}>
                            <span className={css.designImageAnchor}>
                              {Math.abs(imgIdx - designImageIdx) <= 1 ||
                              pinJumpIdxSet.has(imgIdx) ? (
                                <>
                                  <ResponsiveImage
                                    rootClassName={css.designMainImg}
                                    alt={`${title} — photo ${imgIdx + 1}`}
                                    image={img}
                                    variants={['scaled-xlarge', 'scaled-large', 'scaled-medium']}
                                    sizes="(max-width: 1023px) 100vw, 900px"
                                  />
                                  {!pinsHidden &&
                                    (publicData?.designPins || [])
                                      .filter(pin => pin.imageId === img.id?.uuid)
                                      .map((pin, i) => {
                                        const pinProductListing = (
                                          designProductListings || []
                                        ).find(l => l.id.uuid === pin.productId);
                                        const pinTag =
                                          (publicData?.designProducts || []).find(
                                            t => t.id === pin.productId
                                          ) || offCatalogProducts.find(t => t.id === pin.productId);
                                        const pinTitle =
                                          pinProductListing?.attributes?.title ||
                                          pinTag?.title ||
                                          '';
                                        const isOffPin =
                                          typeof pin.productId === 'string' &&
                                          pin.productId.startsWith('off_');
                                        const pinInner = (
                                          <>
                                            <span className={css.designPinDot} />
                                            <span className={css.designPinLabel}>{pinTitle}</span>
                                          </>
                                        );
                                        const isActivePin = activeProductId === pin.productId;
                                        const pinClasses = classNames(css.designPin, {
                                          [css.designPinActive]: isActivePin,
                                        });
                                        const hoverHandlers = {
                                          onMouseEnter: () => {
                                            setActiveProductId(pin.productId);
                                            window.clearTimeout(pinHoverTimer.current);
                                            pinHoverTimer.current = window.setTimeout(
                                              () => scrollRailRowIntoView(pin.productId),
                                              140
                                            );
                                          },
                                          onMouseLeave: () => {
                                            window.clearTimeout(pinHoverTimer.current);
                                            setActiveProductId(null);
                                          },
                                        };
                                        return pinProductListing ? (
                                          <a
                                            key={`${pin.productId}-${i}`}
                                            className={pinClasses}
                                            style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                                            href={pathByRouteName(
                                              'ListingPage',
                                              routeConfiguration,
                                              {
                                                id: pin.productId,
                                                slug: createSlug(pinTitle || 'listing'),
                                              }
                                            )}
                                            onClick={quickViewClickHandler(pin.productId)}
                                            {...hoverHandlers}
                                          >
                                            {pinInner}
                                          </a>
                                        ) : isOffPin ? (
                                          <button
                                            key={`${pin.productId}-${i}`}
                                            type="button"
                                            className={classNames(pinClasses, css.designPinButton)}
                                            style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                                            onClick={() => openMaterialQuickView(pin.productId)}
                                            {...hoverHandlers}
                                          >
                                            {pinInner}
                                          </button>
                                        ) : (
                                          <span
                                            key={`${pin.productId}-${i}`}
                                            className={pinClasses}
                                            style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                                            {...hoverHandlers}
                                          >
                                            {pinInner}
                                          </span>
                                        );
                                      })}
                                </>
                              ) : null}
                            </span>
                          </div>
                        ))}
                      </div>
                      {/* gallery-level overlays: they stay put while photos swipe */}
                      {designGalleryImages.length > 1 ? (
                        <span className={css.designPhotoCount}>
                          {designImageIdx + 1} / {designGalleryImages.length}
                        </span>
                      ) : null}
                      {(publicData?.designPins || []).length > 0 ? (
                        <button
                          type="button"
                          className={css.designPinToggle}
                          onClick={() => setPinsHidden(v => !v)}
                          aria-pressed={pinsHidden}
                        >
                          <FormattedMessage
                            id={
                              pinsHidden
                                ? 'ListingPage.designPinsShow'
                                : 'ListingPage.designPinsHide'
                            }
                          />
                        </button>
                      ) : null}
                    </div>
                    {designGalleryImages.length > 1 ? (
                      <div className={css.designThumbs}>
                        {designGalleryImages.map((img, i) => (
                          <button
                            key={img.id?.uuid || i}
                            type="button"
                            className={classNames(css.designThumb, {
                              [css.designThumbActive]: i === designImageIdx,
                            })}
                            onClick={() => {
                              // jump-cut like before; instant scroll also avoids the
                              // smooth-scroll-cancelled-by-slide-remount trap
                              setDesignImageIdx(i);
                              const el = designHeroTrackRef.current;
                              if (el) el.scrollTo({ left: i * el.clientWidth });
                            }}
                            aria-label={`Photo ${i + 1}`}
                          >
                            <ResponsiveImage
                              rootClassName={css.designThumbImg}
                              alt={`${title} thumbnail ${i + 1}`}
                              image={img}
                              variants={['listing-card', 'scaled-small']}
                              sizes="80px"
                            />
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {ensuredAuthor?.id?.uuid ? (
                  <div className={css.designerCard}>
                    <NamedLink
                      className={css.designerCardMain}
                      name="ProfilePage"
                      params={{ id: ensuredAuthor.id.uuid }}
                    >
                      <AvatarLarge
                        className={css.designerCardAvatar}
                        user={ensuredAuthor}
                        disableProfileLink
                      />
                      <span className={css.designerCardInfo}>
                        <span className={css.designerCardLabel}>
                          <FormattedMessage id="ListingPage.designedBy" />
                        </span>
                        <span className={css.designerCardName}>
                          {authorDisplayName}
                          <FoundingBadge user={ensuredAuthor} />
                        </span>
                      </span>
                    </NamedLink>
                    <NamedLink
                      className={css.designerCardButton}
                      name="ProfilePage"
                      params={{ id: ensuredAuthor.id.uuid }}
                    >
                      <FormattedMessage id="ListingPage.designViewProfile" />
                    </NamedLink>
                  </div>
                ) : null}
                {!isOwnListing && currentListing.id ? (
                  <div className={classNames(css.listingActions, css.designActionsRow)}>
                    <SaveButton listingId={currentListing.id.uuid} size="page" />
                    <button
                      type="button"
                      className={css.designMessageButton}
                      onClick={onContactUser}
                    >
                      <FormattedMessage id="ListingPage.messageDesigner" />
                    </button>
                  </div>
                ) : null}
                {designFactRows.length > 0 ? (
                  <section
                    className={classNames(css.fixedSpecsSection, css.designSpecsSection)}
                    aria-label="Project facts"
                  >
                    <ul className={css.fixedSpecsList}>
                      {designFactRows.map(row => (
                        <li key={row.key} className={css.fixedSpecRow}>
                          <span className={css.fixedSpecLabel}>{row.label}</span>
                          <span className={css.fixedSpecValue}>{row.value}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}
                {description ? (
                  <section className={css.designAbout}>
                    <h2 className={css.designSectionTitle}>
                      <FormattedMessage id="ListingPage.designAboutTitle" />
                    </h2>
                    <SectionTextMaybe text={description} showAsIngress />
                  </section>
                ) : null}
                <div className={css.designAuthorSection}>
                  <SectionAuthorMaybe
                    hideUserCard
                    title={title}
                    listing={currentListing}
                    authorDisplayName={authorDisplayName}
                    onContactUser={onContactUser}
                    isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                    onCloseInquiryModal={() => setInquiryModalOpen(false)}
                    sendInquiryError={sendInquiryError}
                    sendInquiryInProgress={sendInquiryInProgress}
                    onSubmitInquiry={onSubmitInquiry}
                    currentUser={currentUser}
                    onManageDisableScrolling={onManageDisableScrolling}
                  />
                </div>
              </div>
              <aside className={css.designRailCol}>
                {(publicData?.designProducts || []).length > 0 ? (
                  <div className={css.designRailSticky}>
                    <p className={css.designSideCardLabel}>
                      <FormattedMessage id="ListingPage.designRailTitle" />
                    </p>
                    <ul className={css.designRailList} ref={railListRef}>
                      {[
                        ...(publicData?.designProducts || []).map(tag => ({ tag, isOff: false })),
                        ...offCatalogProducts.map(tag => ({ tag, isOff: true })),
                      ].map(({ tag, isOff }) => {
                        const railListing = isOff
                          ? null
                          : (designProductListings || []).find(l => l.id.uuid === tag.id);
                        const railImage = isOff
                          ? (currentListing.images || []).find(
                              img => img.id?.uuid === tag.imageId
                            ) || null
                          : railListing?.images?.[0] || null;
                        const railTitle = railListing?.attributes?.title || tag.title;
                        const railBrand = isOff
                          ? tag.brand || null
                          : railListing?.author?.attributes?.profile?.displayName ||
                            railListing?.attributes?.publicData?.brand ||
                            null;
                        const railPins = (publicData?.designPins || []).filter(
                          pin => pin.productId === tag.id
                        );
                        const firstPin = railPins[0];
                        const isActiveRow = activeProductId === tag.id;
                        // Jump the hero to the photo this material is pinned on and
                        // light the pin. Hover uses a 160ms intent delay; leaving
                        // clears the glow but keeps the photo.
                        const showPin = () => {
                          setPinsHidden(false);
                          if (firstPin) {
                            const imgIdx = designGalleryImages.findIndex(
                              img => img.id?.uuid === firstPin.imageId
                            );
                            if (imgIdx >= 0) {
                              setDesignImageIdx(imgIdx);
                              // ALSO scroll the hero track to that slide (same
                              // jump-cut as a thumbnail click). Without this the
                              // index moved but the track stayed on the old slide
                              // — whose image had just UNMOUNTED (slides window
                              // current ±1), leaving a blank white hero.
                              const el = designHeroTrackRef.current;
                              if (el) el.scrollTo({ left: imgIdx * el.clientWidth });
                            }
                          }
                          setActiveProductId(tag.id);
                        };
                        const enterRow = () => {
                          window.clearTimeout(railHoverTimer.current);
                          railHoverTimer.current = window.setTimeout(showPin, 160);
                        };
                        const leaveRow = () => {
                          window.clearTimeout(railHoverTimer.current);
                          setActiveProductId(null);
                        };
                        const rowInner = (
                          <>
                            <span className={css.designRailImageWrap}>
                              {railImage ? (
                                <ResponsiveImage
                                  rootClassName={css.designRailImage}
                                  alt={railTitle}
                                  image={railImage}
                                  variants={['listing-card', 'square-small']}
                                  sizes="72px"
                                />
                              ) : (
                                <span className={css.designRailImageFallback} />
                              )}
                            </span>
                            <span className={css.designRailInfo}>
                              <span className={css.designRailTitle}>{railTitle}</span>
                              {railBrand ? (
                                <span className={css.designRailBrand}>{railBrand}</span>
                              ) : null}
                              {tag.note ? (
                                <span className={css.designRailNote}>{tag.note}</span>
                              ) : null}
                              {isOff ? (
                                <span className={css.designRailOffBadge}>
                                  <FormattedMessage id="ListingPage.offCatalogBadge" />
                                </span>
                              ) : null}
                            </span>
                          </>
                        );
                        const rowClasses = classNames(css.designRailRow, {
                          [css.designRailRowActive]: isActiveRow,
                        });
                        return (
                          <li key={tag.id} id={`design-material-${tag.id}`}>
                            {railListing ? (
                              <a
                                className={rowClasses}
                                href={pathByRouteName('ListingPage', routeConfiguration, {
                                  id: tag.id,
                                  slug: createSlug(railTitle || 'listing'),
                                })}
                                onClick={quickViewClickHandler(tag.id)}
                                onMouseEnter={enterRow}
                                onMouseLeave={leaveRow}
                              >
                                {rowInner}
                              </a>
                            ) : isOff ? (
                              <button
                                type="button"
                                className={classNames(rowClasses, css.designRailRowButton)}
                                onClick={() => openMaterialQuickView(tag.id)}
                                onMouseEnter={enterRow}
                                onMouseLeave={leaveRow}
                              >
                                {rowInner}
                              </button>
                            ) : (
                              <span
                                className={rowClasses}
                                onMouseEnter={enterRow}
                                onMouseLeave={leaveRow}
                              >
                                {rowInner}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : null}
              </aside>
            </div>
            <Modal
              id="ListingPage.designMaterialQuickView"
              isOpen={!!quickViewListing || !!quickViewOffTag}
              onClose={() => setQuickViewProductId(null)}
              onManageDisableScrolling={onManageDisableScrolling}
              usePortal
              containerClassName={css.designQuickViewModal}
              scrollLayerClassName={css.designQuickViewScrollLayer}
            >
              {quickViewOffTag ? (
                <div className={css.designQuickView}>
                  <div className={css.designQuickViewImageCol}>
                    <div className={css.designQuickViewImageBox}>
                      {quickViewOffImage ? (
                        <ResponsiveImage
                          rootClassName={css.designQuickViewImage}
                          alt={quickViewOffTag.title || ''}
                          image={quickViewOffImage}
                          variants={['scaled-large', 'scaled-medium', 'listing-card']}
                          sizes="(max-width: 767px) 100vw, 420px"
                        />
                      ) : (
                        <div className={css.designQuickViewImagePlaceholder} />
                      )}
                    </div>
                  </div>
                  <div className={css.designQuickViewInfoCol}>
                    <h2 className={css.designQuickViewTitle}>{quickViewOffTag.title}</h2>
                    {quickViewOffTag.brand ? (
                      <p className={css.designQuickViewBrand}>{quickViewOffTag.brand}</p>
                    ) : null}
                    {quickViewOffTag.note ? (
                      <p className={css.designQuickViewNote}>
                        <span className={css.designQuickViewNoteLabel}>
                          <FormattedMessage id="ListingPage.designQuickViewUsedFor" />
                        </span>
                        {quickViewOffTag.note}
                      </p>
                    ) : null}
                    <p className={css.designQuickViewOffNote}>
                      <FormattedMessage id="ListingPage.offCatalogQuickViewNote" />
                    </p>
                  </div>
                </div>
              ) : quickViewListing ? (
                <div
                  className={css.designQuickView}
                  onTouchStart={e => {
                    // photo-carousel swipes shouldn't drag the sheet around
                    if (e.target.closest?.("[class*='designQuickViewTrack']")) return;
                    const el = e.currentTarget.closest("[class*='designQuickViewModal']");
                    // only arm the gesture when the sheet is scrolled to its top,
                    // so the sheet's own scrolling still works
                    if (el && el.scrollTop <= 0) {
                      sheetDrag.current = {
                        startY: e.touches[0].clientY,
                        el,
                        dragging: true,
                      };
                      el.style.transition = 'none';
                    }
                  }}
                  onTouchMove={e => {
                    const d = sheetDrag.current;
                    if (!d.dragging || !d.el) return;
                    const delta = e.touches[0].clientY - d.startY;
                    if (delta > 0) {
                      d.el.style.transform = `translateY(${delta}px)`;
                    }
                  }}
                  onTouchEnd={e => {
                    const d = sheetDrag.current;
                    if (!d.dragging || !d.el) return;
                    const delta = (e.changedTouches[0]?.clientY || d.startY) - d.startY;
                    d.el.style.transition = 'transform 0.2s ease';
                    if (delta > 90) {
                      d.el.style.transform = 'translateY(100%)';
                      window.setTimeout(() => {
                        d.el.style.transform = '';
                        d.el.style.transition = '';
                        setQuickViewProductId(null);
                      }, 180);
                    } else {
                      d.el.style.transform = '';
                    }
                    sheetDrag.current = { startY: 0, el: null, dragging: false };
                  }}
                >
                  <div className={css.designQuickViewImageCol}>
                    <div className={css.designQuickViewImageBox}>
                      {/* swipe carousel through the product photos; thumbs below
                          jump the track and highlight follows a swipe */}
                      <div className={css.designQuickViewTrack} ref={quickViewTrackRef}>
                        {(quickViewListing.images || []).slice(0, 6).map((img, i) => (
                          <div key={img.id?.uuid || i} className={css.designQuickViewSlide}>
                            {Math.abs(i - quickViewImageIdx) <= 1 ? (
                              <ResponsiveImage
                                rootClassName={css.designQuickViewImage}
                                alt={`${quickViewListing.attributes?.title || ''} — photo ${i + 1}`}
                                image={img}
                                variants={['scaled-large', 'scaled-medium', 'listing-card']}
                                sizes="(max-width: 767px) 100vw, 420px"
                              />
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                    {(quickViewListing.images || []).length > 1 ? (
                      <div className={css.designQuickViewThumbs}>
                        {(quickViewListing.images || []).slice(0, 6).map((img, i) => (
                          <button
                            key={img.id?.uuid || i}
                            type="button"
                            className={classNames(css.designQuickViewThumb, {
                              [css.designQuickViewThumbActive]: i === quickViewImageIdx,
                            })}
                            onClick={() => {
                              setQuickViewImageIdx(i);
                              const el = quickViewTrackRef.current;
                              if (el) el.scrollTo({ left: i * el.clientWidth });
                            }}
                            aria-label={`Product photo ${i + 1}`}
                          >
                            <ResponsiveImage
                              rootClassName={css.designQuickViewThumbImg}
                              alt=""
                              image={img}
                              variants={['listing-card', 'square-small']}
                              sizes="48px"
                            />
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className={css.designQuickViewInfoCol}>
                    <h2 className={css.designQuickViewTitle}>
                      {quickViewListing.attributes?.title}
                    </h2>
                    {quickViewBrand ? (
                      <p className={css.designQuickViewBrand}>{quickViewBrand}</p>
                    ) : null}
                    {quickViewTag?.note ? (
                      <p className={css.designQuickViewNote}>
                        <span className={css.designQuickViewNoteLabel}>
                          <FormattedMessage id="ListingPage.designQuickViewUsedFor" />
                        </span>
                        {quickViewTag.note}
                      </p>
                    ) : null}
                    {quickViewListing.attributes?.description ? (
                      <p className={css.designQuickViewDescription}>
                        {quickViewListing.attributes.description}
                      </p>
                    ) : null}
                    <div className={css.designQuickViewActions}>
                      <a
                        className={css.designQuickViewLink}
                        href={quickViewPath}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FormattedMessage id="ListingPage.designQuickViewOpenProduct" />
                      </a>
                    </div>
                  </div>
                </div>
              ) : null}
            </Modal>
          </div>
        ) : (
          <div className={css.contentWrapperForProductLayout}>
            <div className={css.mainColumnForProductLayout}>
              {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
                <ActionBarMaybe
                  className={css.actionBarForProductLayout}
                  isOwnListing={isOwnListing}
                  listing={currentListing}
                  showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                  currentUser={currentUser}
                />
              ) : null}
              {mounted && currentListing.id ? (
                <ActionBarMaybe
                  className={css.actionBarForProductLayout}
                  isOwnListing={isOwnListing}
                  listing={currentListing}
                  currentUser={currentUser}
                  editParams={{
                    id: listingId.uuid,
                    slug: listingSlug,
                    type: listingPathParamType,
                    tab: listingTab,
                  }}
                />
              ) : null}
              {/* Breadcrumb trail: Home › L1 label › L2 label › L3 label
                › Listing title. Renders above the gallery; segments
                are silently dropped if a level is missing from the
                tree (defensive against stale taxonomy IDs). */}
              <Breadcrumbs
                publicData={publicData}
                categoriesConfig={config.categoryConfiguration?.categories}
                title={title}
              />
              {showListingImage && (
                <SectionGallery
                  listing={currentListing}
                  variantPrefix={config.layout.listingImage.variantPrefix}
                  preselectedVariantSku={activeVariant?.sku}
                  onVariantSwipe={setGallerySwipeSku}
                />
              )}

              {/* Variant picker for manufactured listings. Renders nothing if
                the listing has no variants (parallel-SKU mode) or its
                category doesn't support variants — a no-op for the existing
                flat catalogue. Selecting a variant swaps the gallery image
                (via preselectedVariantSku above) and syncs the ?sku= link. */}
              <SectionVariantPicker
                listing={currentListing}
                initialSku={initialVariantSku}
                onVariantChange={setActiveVariant}
                syncSku={gallerySwipeSku}
              />

              {/* Selected-variant SKU. Follows the picker selection; flat
                listings (no variants / no picker) fall back to the listing's
                own publicData.sku. Lives HERE (main content column) — never
                inside OrderPanel, which ModalInMobile also renders inline on
                mobile and would duplicate the line. Synthetic legacy codes
                (BP+digits, pre-resku Buster + Punch uploads) are internal ids,
                not supplier SKUs — hidden. */}
              {(() => {
                const skuToShow = activeVariant?.sku || publicData.sku;
                const isInternalCode = typeof skuToShow === 'string' && /^BP\d{8,}/.test(skuToShow);
                return skuToShow && !isInternalCode ? (
                  <p className={css.variantSkuLine}>SKU: {skuToShow}</p>
                ) : null;
              })()}

              {/* Save + Add to Board action row (only for non-owners) */}
              {!isOwnListing && currentListing.id && (
                <div className={css.listingActions}>
                  <SaveButton
                    listingId={currentListing.id.uuid}
                    size="page"
                    variantSku={activeVariant?.sku}
                  />
                  <button
                    className={isAddedToBoard ? css.addToBoardButtonAdded : css.addToProjectButton}
                    onClick={() => {
                      if (!isAuthenticated) {
                        // Router push carrying `from` (Routes.js mechanism,
                        // consumed by AuthenticationPage) so login returns to
                        // this listing instead of the homepage. Same fix as
                        // SaveButton, which sits right next to this button.
                        history.push({
                          pathname: '/login',
                          state: {
                            from: `${location.pathname}${location.search}${location.hash}`,
                          },
                        });
                      } else if (isEmailUnverified(currentUser)) {
                        // Signed up but unverified → verify-email gate modal
                        dispatch(showVerifyEmailGate());
                      } else {
                        setBoardModalOpen(true);
                      }
                    }}
                    type="button"
                  >
                    {isAddedToBoard ? (
                      <FormattedMessage id="ListingPage.addedToBoard" />
                    ) : (
                      <FormattedMessage id="ListingPage.addToBoard" />
                    )}
                  </button>
                  <AddToBoardModal
                    listingId={currentListing.id.uuid}
                    listing={currentListing}
                    preselectedVariantSku={activeVariant?.sku}
                    isOpen={boardModalOpen}
                    onClose={() => setBoardModalOpen(false)}
                    onManageDisableScrolling={onManageDisableScrolling}
                  />
                </div>
              )}

              <div
                className={showListingImage ? css.mobileHeading : css.noListingImageHeadingProduct}
              >
                {showListingImage ? (
                  <H4 as="h1" className={css.orderPanelTitle}>
                    <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                  </H4>
                ) : (
                  <H3 as="h1" className={css.orderPanelTitle}>
                    <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                  </H3>
                )}
                {/* "By <brand name>" line — links the brand name to its profile
                  page. Renders only when both a display name + author id are
                  available (anonymous / missing-id listings just hide the
                  whole byline rather than rendering a non-clickable name). */}
                {authorDisplayName && ensuredAuthor.id?.uuid ? (
                  <p className={css.byLine}>
                    <FormattedMessage
                      id="ListingPage.byAuthor"
                      defaultMessage="By {authorLink}"
                      values={{
                        authorLink: (
                          <NamedLink
                            className={css.byLineLink}
                            name="ProfilePage"
                            params={{ id: ensuredAuthor.id.uuid }}
                          >
                            {authorDisplayName}
                          </NamedLink>
                        ),
                      }}
                    />
                  </p>
                ) : null}
              </div>
              {/* Product page structure (May 2026):
                  • Fixed spec bar — always-visible quick facts: in_stock,
                    samples_available, colour, price_tier, interior_exterior
                  • Four collapsible dropdown sections — Description (open
                    by default), Specifications, Files, Certifications.
                Specifications/Files/Certifications data sources are TBD;
                rendered as empty dropdowns awaiting field wiring. */}
              <SectionFixedSpecs
                publicData={publicData}
                listingFieldConfigs={listingConfig.listingFields}
              />

              <CollapsibleSection
                title={
                  <FormattedMessage
                    id="ListingPage.section.description"
                    defaultMessage="Description"
                  />
                }
                defaultOpen={true}
              >
                {description ? (
                  <SectionTextMaybe text={description} showAsIngress />
                ) : (
                  <p className={css.sectionPlaceholder}>
                    <FormattedMessage
                      id="ListingPage.section.descriptionEmpty"
                      defaultMessage="No description provided."
                    />
                  </p>
                )}
              </CollapsibleSection>

              <CollapsibleSection
                title={
                  <FormattedMessage
                    id="ListingPage.section.specifications"
                    defaultMessage="Specifications"
                  />
                }
              >
                <SpecsBody publicData={publicData} />
              </CollapsibleSection>

              <CollapsibleSection
                title={<FormattedMessage id="ListingPage.section.files" defaultMessage="Files" />}
              >
                <FilesBody publicData={publicData} />
              </CollapsibleSection>

              <CollapsibleSection
                title={
                  <FormattedMessage id="ListingPage.section.features" defaultMessage="Features" />
                }
              >
                <FeaturesBody publicData={publicData} />
              </CollapsibleSection>

              <CollapsibleSection
                title={
                  <FormattedMessage
                    id="ListingPage.section.certifications"
                    defaultMessage="Certifications"
                  />
                }
              >
                <CertificationsBody publicData={publicData} />
              </CollapsibleSection>

              {/* What's included & warranty — supplier box contents + verified
                warranty terms. Any listing of any brand that carries
                publicData.whats_included / publicData.warranty gets the
                section; hidden entirely when both are absent (unlike the
                sections above, which render a placeholder body). */}
              {hasIncludedWarranty(publicData) ? (
                <CollapsibleSection
                  title={
                    <FormattedMessage
                      id="ListingPage.section.includedWarranty"
                      defaultMessage="What's included & warranty"
                    />
                  }
                >
                  <IncludedWarrantyBody publicData={publicData} />
                </CollapsibleSection>
              ) : null}

              {/* Showcase→catalogue connection: projects that tagged this
                material, studio credited on each. Renders nothing for design
                listings or untagged materials. */}
              <SectionFeaturedProjectsMaybe
                listingId={currentListing.id?.uuid}
                isDesign={isDesignShowcase}
              />

              {/* Matterax: SectionMapMaybe removed — geolocation isn't
                meaningful for a B2B materials catalogue (suppliers ship or
                hold samples, not serve at a physical address). The
                component file is left in the tree, just unused, in case we
                want to restore it for outdoor / installation listings. */}
              {/* Matterax: reviews feature disabled site-wide. Section
                suppressed unconditionally; reviews data still flows through
                the duck (no impact on other consumers) but never renders. */}
              {false && <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />}
              {/* Author block: hidden on desktop via `.authorMobileOnly`
                because the OrderPanel sidebar already renders the brand
                partner card. Mobile keeps it inline (no sidebar there). */}
              <div className={css.authorMobileOnly}>
                <SectionAuthorMaybe
                  title={title}
                  listing={currentListing}
                  authorDisplayName={authorDisplayName}
                  onContactUser={onContactUser}
                  isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                  onCloseInquiryModal={() => setInquiryModalOpen(false)}
                  sendInquiryError={sendInquiryError}
                  sendInquiryInProgress={sendInquiryInProgress}
                  onSubmitInquiry={onSubmitInquiry}
                  currentUser={currentUser}
                  onManageDisableScrolling={onManageDisableScrolling}
                />
              </div>
            </div>
            <div className={css.orderColumnForProductLayout}>
              <OrderPanel
                className={classNames(css.productOrderPanel, {
                  [css.imagesEnabled]: showListingImage,
                })}
                listing={currentListing}
                isOwnListing={isOwnListing}
                onSubmit={handleOrderSubmit}
                authorLink={
                  ensuredAuthor.id?.uuid ? (
                    <NamedLink
                      className={css.authorNameLink}
                      name="ProfilePage"
                      params={{ id: ensuredAuthor.id.uuid }}
                    >
                      {authorDisplayName}
                    </NamedLink>
                  ) : (
                    authorDisplayName
                  )
                }
                title={
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                }
                titleDesktop={
                  <>
                    <H4 as="h1" className={css.orderPanelTitle}>
                      <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                    </H4>
                    {authorDisplayName && ensuredAuthor.id?.uuid ? (
                      <p className={css.byLine}>
                        <FormattedMessage
                          id="ListingPage.byAuthor"
                          defaultMessage="By {authorLink}"
                          values={{
                            authorLink: (
                              <NamedLink
                                className={css.byLineLink}
                                name="ProfilePage"
                                params={{ id: ensuredAuthor.id.uuid }}
                              >
                                {authorDisplayName}
                              </NamedLink>
                            ),
                          }}
                        />
                      </p>
                    ) : null}
                  </>
                }
                payoutDetailsWarning={payoutDetailsWarning}
                author={ensuredAuthor}
                onManageDisableScrolling={onManageDisableScrolling}
                onContactUser={onContactUser}
                {...restOfProps}
                validListingTypes={config.listing.listingTypes}
                marketplaceCurrency={config.currency}
                dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
                marketplaceName={config.marketplaceName}
                showListingImage={showListingImage}
              />
            </div>
          </div>
        )}
        {/* Find-similar: full-width "Similar materials" rail below the
          two-column body. Catalog listings only — launches and design
          showcases carry their own related rails. */}
        {!isLaunch && !isDesignShowcase ? (
          <SectionSimilarListingsMaybe
            listings={similarListings}
            inProgress={similarListingsInProgress}
            anchorId={currentListing?.id?.uuid}
            source={similarListingsSource}
          />
        ) : null}
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    designProductRefs,
    relatedLaunchRefs,
    similarListingRefs,
    fetchSimilarListingsInProgress,
    similarListingsSource,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  // Design showcase / launch: tagged catalog listings for the materials
  // section (designProducts) or the related-products rail (relatedProductIds)
  const designProductListings =
    designProductRefs?.length > 0 ? getMarketplaceEntities(state, designProductRefs) : [];

  // "What's New": sibling live launches for the read-next rail
  const relatedLaunchListings =
    relatedLaunchRefs?.length > 0 ? getMarketplaceEntities(state, relatedLaunchRefs) : [];

  // Find-similar: ranked neighbours for the "Similar materials" rail
  // (refs preserve the server's rank order through the entity store)
  const similarListings =
    similarListingRefs?.length > 0 ? getMarketplaceEntities(state, similarListingRefs) : [];

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    designProductListings,
    relatedLaunchListings,
    similarListings,
    similarListingsInProgress: fetchSimilarListingsInProgress,
    similarListingsSource,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
    // Raw boards → the component derives per-variant "Added to board" from the
    // active variant (mapStateToProps can't see the picker state).
    boards: selectBoards(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
